import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getNotifications,
  getUnreadCount,
  markNotificationRead,
  type NotificationItem,
} from '@/services/notifications-api'

// QueryKey sabitleri — test + N2-02 invalidate için export edilir.
export const NOTIFICATIONS_LIST_KEY = ['notifications', 'list'] as const
export const UNREAD_COUNT_KEY = ['notifications', 'unread-count'] as const

/** Bildirim yoklama aralığı (sayaç + toast listesi). Sekmeye/pencereye dönünce ayrıca anında yenilenir. */
export const NOTIFICATION_POLL_MS = 120_000

/**
 * Okunmamış bildirim sayacı — badge her ekranda sürekli mount olduğu için
 * POLLING burada (D-06). refetchInterval ile ~2 dk'da bir taze sayaç çeker;
 * arka sekmede polling durur, sekmeye dönünce (focus) hemen tazelenir.
 * WebSocket YOK — realtime refetchInterval'e taşındı.
 */
export function useUnreadCount(options?: { enabled?: boolean }) {
  return useQuery<number>({
    queryKey: UNREAD_COUNT_KEY,
    queryFn: getUnreadCount,
    refetchInterval: NOTIFICATION_POLL_MS, // 2 dk: prod trafiğinin ~%6'sı yoklamaydı; sekmeye dönünce zaten anında yenilenir (aşağıda)
    refetchIntervalInBackground: false, // arka sekmede polling durur (D-06)
    refetchOnWindowFocus: true, // global false'u override — sekmeye dönünce taze
    staleTime: 30_000,
    // enabled guard: oturum yokken çağırma (use-subscription.ts deseni)
    enabled: options?.enabled ?? true,
  })
}

/**
 * Bildirim listesi — panel açıkken doldurulur (N2-02 open state'iyle gate'lenir).
 * Sürekli polling GEREKMEZ; focus'ta ve panel açılışında tazelenir.
 *
 * N2-03: opsiyonel `refetchInterval` verilirse liste periyodik tazelenir
 * (toast hook always-on poll için). Verilmezse eski davranış (interval yok).
 * `refetchOnReconnect: true` — online olunca (çevrimdışı→çevrimiçi) taze çeker,
 * böylece cihazda daha önce toast görülmemiş okunmamışlar yakalanır (D-T2).
 * NOTIFICATIONS_LIST_KEY aynı → toast hook ile bell AYNI cache'i paylaşır (tek network).
 */
export function useNotificationsList(options?: {
  enabled?: boolean
  refetchInterval?: number
}) {
  return useQuery<NotificationItem[]>({
    queryKey: NOTIFICATIONS_LIST_KEY,
    queryFn: getNotifications,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: options?.refetchInterval,
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  })
}

/**
 * Okundu işaretle mutasyonu. onSuccess → hem liste hem unread-count invalidate
 * edilir; böylece badge ile panel tek-ekranda tutarlı kalır (D-05).
 */
export function useMarkNotificationRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (notificationId: string) => markNotificationRead(notificationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: NOTIFICATIONS_LIST_KEY })
      qc.invalidateQueries({ queryKey: UNREAD_COUNT_KEY })
    },
  })
}
